import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Fleet } from "../../../../../../../models/fleet";
import { Filters, Searches, Sorts } from "../../../../../../../models/shipeezi.table";
import FleetService from "../../../../../../../services/fleet.service";
import { FleetKeys } from "../../../../../../../util/queries";
import useRoutePlannerAction from "./useRoutePlannerActions";
import { useMemo } from "react";

interface UpdateFleet {
  id: number;
  fleet: Fleet;
}

interface CreateFleet {
  fleet: Fleet;
  addToRoutePlanner: boolean;
}

const useAction = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const { useAddToRoutePlanner, useUpdateToRoutePlanner } = useRoutePlannerAction();

  const { mutate: addToRoutePlanner } = useAddToRoutePlanner();
  const { mutate: updateInRoutePlanner } = useUpdateToRoutePlanner();

  const branchesIds = useMemo(() => {
      if (!currentUser || !currentUser?.branches || !currentUser?.companyId) {
        return [];
      }

      return currentUser.branches.filter(branch => branch.companyId === currentUser.companyId)?.map(branch => branch.id!)
    }, [currentUser?.branches])

  const getAllFleet = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters,
    sorting?: Sorts
  ) => {
    return useQuery({
      queryKey: FleetKeys.getAll([page, pageSize, filters, searches, sorting, currentUser?.branches]),
      queryFn: async () => {
        const response = await FleetService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          sortBy: sorting
            ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
            : JSON.stringify([["updatedAt", "desc"]]),
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify([
            { association: "branches", required: false, where: {id: currentUser?.branches?.map((branch) => branch.id)} },
            { association: "fleetType" },
            { association: "warehouse", attributes: ["id", "name", "externalUuid"] },
            { association: "endWarehouse", attributes: ["id", "name", "externalUuid"] },
          ]),
          ...filters,
        });

        if (response && response.data) return response.data;

        return [];
      },
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
    });
  };

  const countFleet = (filters?: Filters, searches?: Searches) => {
    return useQuery({
      queryKey: FleetKeys.count([filters, searches, currentUser?.branches]),
      queryFn: async () => {
        const response = await FleetService.count({
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify([
            { association: "branches", required: false, where: {id: branchesIds} },
          ]),
          ...filters,
        });

        if (response && response.data) return response.data;

        return 0;
      },
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
    });
  };

  const useCreateFleet = (callbackSuccess?: () => void) => {
    return useMutation({
      mutationKey: FleetKeys.create(),
      mutationFn: (variables: CreateFleet) => FleetService.create(variables.fleet),
      onSuccess: (response: AxiosResponse<Fleet>, variables: CreateFleet) => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));

        if (currentUser?.company.useRoutePlanner) {
          const { addToRoutePlanner: toAdd, fleet } = variables;

          if (toAdd) addToRoutePlanner({ ...fleet, id: response?.data?.id });
        }

        if (callbackSuccess) callbackSuccess();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  const useUpdateFleet = (callbackSuccess?: () => void) => {
    return useMutation({
      mutationKey: FleetKeys.update(),
      mutationFn: ({ id, fleet }: UpdateFleet) => FleetService.update(id, fleet),
      onSuccess: (_: AxiosResponse<boolean>, variables: UpdateFleet) => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.notification.success.fleet",
        });

        if (currentUser?.company.useRoutePlanner && variables?.fleet.externalUuid) {
          updateInRoutePlanner({ id: variables?.fleet.externalUuid, fleet: variables.fleet });
        }

        if (callbackSuccess) callbackSuccess();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    });
  };

  return {
    getAllFleet,
    countFleet,
    useCreateFleet,
    useUpdateFleet,
  };
};

export default useAction;
